import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Subject } from 'rxjs';
import { AppDispatcher } from '../../store';
import { AccountDataStore } from '../../modules/account-data/account-data.store';
import { AuthDataStore } from '../../modules/auth-data/auth-data.store';
import { ErrorService } from '../../modules/error-reporting/error.service';
import { FeaturesDataStore } from '../../modules/features-data/features-data.store';
import { HttpService } from '../../modules/http/http.service';
import { LocalizationService } from '../../modules/localization/localization.service';
import { NavigationService } from '../../modules/navigation/navigation.service';
import { SettingsDataStore } from '../../modules/settings-data/settings-data.store';
// eslint-disable-next-line max-len
import { IPreventWindowUnloadingOptions } from '../../modules/shared/directives/prevent-window-unloading/prevent-window-unloading.directive';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { I2gNgPerformanceService, MarkerNamesEnum } from 'i2g-ng-performance';
import { AppBuilder } from './app.builder';
import { Settings } from '../../modules/settings-data/settings.model';
import { I2gNgTrackingService } from 'i2g-ng-tracking';
import { SettingsDataDispatcher } from '../../modules/settings-data/settings-data.dispatcher';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [AppBuilder],
})
export class AppComponent implements OnInit, OnDestroy {
  private _destroyed: Subject<boolean> = new Subject<boolean>();

  preventWindowUnloadingOptions: IPreventWindowUnloadingOptions;

  constructor(
    private _router: Router,
    private _httpService: HttpService,
    private _trackingService: I2gNgTrackingService,
    private _settingsDataStore: SettingsDataStore,
    private _settingsDataDispatcher: SettingsDataDispatcher,
    private _accountDataStore: AccountDataStore,
    private _featuresDataStore: FeaturesDataStore,
    private _navigationService: NavigationService,
    private _errorService: ErrorService,
    private _route: ActivatedRoute,
    private _authDataStore: AuthDataStore,
    private _appDispatcher: AppDispatcher,
    private _localizationService: LocalizationService,
    private _i2gPerformanceService: I2gNgPerformanceService,
    private _appBuilder: AppBuilder
  ) {}

  ngOnInit() {
    this._i2gPerformanceService.mark(MarkerNamesEnum.time_to_app_load);

    this._localizationService
      .isTranslationsReady()
      .subscribe(() => this._appDispatcher.initApp());

    this.preventWindowUnloadingOptions = {
      onBeforeUnload: this._checkForOutboundHttpRequests.bind(this),
    };

    this._route.queryParamMap
      .pipe(
        map((queryParamMap) => queryParamMap.get('deepLink')),
        filter((x) => !!x)
      )
      .subscribe(
        (deepLinkParam: string) =>
          this._navigationService.goToDeepLink(
            decodeURIComponent(deepLinkParam)
          ),
        (error) => console.error(error) // TODO: handle errors properly
      );

    const user$ = this._accountDataStore.getUser();

    // eslint-disable-next-line @typescript-eslint/prefer-regexp-exec
    if (!this._router.url.match(/^\/billing(?!\/manage-renew)/)) {
      forkJoin(
        this._authDataStore.getAccountId(),
        this._authDataStore.getUserId(),
        user$,
        this._settingsDataStore.settings$.pipe(
          filter<Settings>(Boolean),
          take(1)
        ),
        this._featuresDataStore.getFeatures(),
        this._featuresDataStore.getSubscription()
      )
        .pipe(takeUntil(this._destroyed))
        .subscribe(
          ([accountId, userId, user, settings, features, subscription]) => {
            const trackingTraits = this._appBuilder.buildAppTrackingTraits({
              accountId,
              userId,
              user,
              settings,
              features,
              subscription,
            });

            const uniqueId = this._trackingService.generateUniqueId(
              userId,
              accountId
            );
            this._trackingService.identify(uniqueId, trackingTraits);
          }
        );
    }

    forkJoin(this._accountDataStore.getAccount(), user$)
      .pipe(takeUntil(this._destroyed))
      .subscribe(([account, user]) => this._errorService.init(account, user));

    // fetch required content for I2g Money
    this._settingsDataDispatcher.fetchI2gMoneyWebview();
    this._settingsDataDispatcher.fetchI2gMoneyContext();
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }

  private _checkForOutboundHttpRequests() {
    return this._httpService.anyOutboundRequests();
  }
}
